/* session-overrides.js
 *
 * PREVIEW-ONLY testing overlay. Lets an operator open a preview/unpublished
 * theme with URL params that make the storefront behave as if it already knows
 * a person — WITHOUT writing anything real and WITHOUT persisting past the tab.
 *
 *   ?email=x@gmail.com   → the store believes this email is the logged-in
 *                          customer (feeds window.AGB_Customer, which TTB +
 *                          signup-vid-link read). NOTHING is stored.
 *   ?country=CA          → readers of localStorage['country'] (TTB country
 *                          patch, best-seller, returning-user flows) see CA,
 *                          but it is NEVER written to storage.
 *   ?reset_popup=1       → clears the giveaway/offer "already seen" keys
 *                          (ag_games_*) so those flows show fresh.
 *   ?sim_off=1           → exit: forget the simulation immediately.
 *
 * While a simulation is active the preview build dot (the green marker) gets an
 * amber ring; click it for who is simulated, the blocked writes and "Exit
 * simulation". No bar across the page (removed 2026-10-08).
 *
 * THREE guarantees (the whole point):
 *   1. NO LEAKS. While a simulation is active a NETWORK FIREWALL wraps fetch +
 *      sendBeacon and drops every cross-origin write (identity, analytics,
 *      restock, Drip, the retiring db-app) — an allowlist, so a writer we don't
 *      know about fails CLOSED. Same-origin cart note-attribute stamps have the
 *      identity stripped so a real checkout can't tag the order with the fake
 *      person. Reads (GET, config, product JSON) pass untouched.
 *   2. NO PERSISTENCE. The only thing written is a namespaced sessionStorage key
 *      so the sim survives navigation within the tab; it dies on tab close and
 *      never reaches a server. Injected email/country live in memory only.
 *   3. PREVIEW ONLY. Arms solely on a non-published theme (Shopify.theme.role
 *      !== 'main') or a preview host. On the LIVE store this file is a total
 *      no-op — the params do nothing, so shoppers can never trigger it.
 *
 * Load FIRST among the custom scripts (before recommendations.js) so it strips
 * ?email= before that file's save-visitor hijack reads it, sets the customer
 * before theme.liquid's inline AGB_Customer line, and installs the firewall
 * before any writer loads. Self-contained (isolation principle); raw-loaded,
 * no minify. Params are stripped from the URL on arrival so they never reach
 * analytics, referrers, history or a screenshot.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  // ---- PREVIEW GATE: never arms on the published/live store ----------------
  function isPreview() {
    try {
      var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      if (role === 'main') return false;        // published theme => NEVER
      if (role) return true;                     // unpublished / development / demo
      var h = (window.location.hostname || '');
      return /\.shopifypreview\.com$/.test(h) || /\.myshopify\.com$/.test(h) ||
             h === 'localhost' || h === '127.0.0.1';
    } catch (e) { return false; }
  }
  if (!isPreview()) return;                       // LIVE: total no-op

  var SS_KEY = '__AGB_sim';
  var OUR_PARAMS = ['email', 'country', 'reset_popup', 'sim_off'];

  function readState() {
    try { return JSON.parse(window.sessionStorage.getItem(SS_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function writeState(s) {
    try { window.sessionStorage.setItem(SS_KEY, JSON.stringify(s)); } catch (e) {}
  }

  // ---- consume + STRIP our URL params --------------------------------------
  var state = readState();
  var doResetPopup = false, doOff = false, urlChanged = false, url = null, params = null;
  try { url = new URL(window.location.href); params = url.searchParams; } catch (e) {}
  if (params) {
    if (params.get('sim_off') === '1') doOff = true;
    var em = params.get('email');   if (em) state.email = em.replace(/\s/g, '+');
    var co = params.get('country'); if (co) state.country = co;
    if (params.get('reset_popup') === '1') doResetPopup = true;
    for (var i = 0; i < OUR_PARAMS.length; i++) {
      if (params.has(OUR_PARAMS[i])) { params.delete(OUR_PARAMS[i]); urlChanged = true; }
    }
    if (urlChanged) {
      try {
        var q = params.toString();
        window.history.replaceState(null, '', url.pathname + (q ? '?' + q : '') + url.hash);
      } catch (e) {}
    }
  }

  if (doOff) { try { window.sessionStorage.removeItem(SS_KEY); } catch (e) {} return; }

  writeState(state);

  // ---- popup reset: clear the giveaway/offer "already seen" keys -----------
  if (doResetPopup) {
    try {
      var kill = [];
      for (var j = 0; j < window.localStorage.length; j++) {
        var k = window.localStorage.key(j);
        if (k && k.indexOf('ag_games_') === 0) kill.push(k);
      }
      for (var m = 0; m < kill.length; m++) window.localStorage.removeItem(kill[m]);
    } catch (e) {}
  }

  var active = !!(state.email || state.country);
  if (!active) return;   // reset-only or nothing to inject: no firewall needed

  // ---- inject "we already know this email" ---------------------------------
  // theme.liquid's inline block reads window.__AGB_simCustomer first (see the
  // AGB_Customer line), so this wins over the real (null, in incognito) value.
  if (state.email) {
    window.__AGB_simCustomer = { email: state.email, id: null };
    window.AGB_Customer = window.__AGB_simCustomer;
  }

  // ---- inject country WITHOUT writing it -----------------------------------
  // Shim getItem so readers see it; swallow any write TO 'country' so a
  // consumer's setItem('country', …) can't persist the fake value.
  if (state.country) {
    try {
      var _get = window.localStorage.getItem.bind(window.localStorage);
      var _set = window.localStorage.setItem.bind(window.localStorage);
      window.localStorage.getItem = function (key) {
        if (key === 'country') return state.country;
        return _get(key);
      };
      window.localStorage.setItem = function (key, val) {
        if (key === 'country') return;             // swallow — never persist the injected country
        return _set(key, val);
      };
    } catch (e) {}
  }

  // ---- NETWORK FIREWALL: no write leaves the browser while simulating ------
  var blocked = 0;
  function isWriteMethod(m) {
    m = (m || 'GET').toUpperCase();
    return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
  }
  function sameOrigin(u) {
    try { return new URL(u, window.location.href).origin === window.location.origin; }
    catch (e) { return true; }                     // unparseable => treat as same-origin (fail safe, don't block store JS)
  }
  // Writes that carry no identity and are allowed through even while simulating (Dorian 2026-10-26: the Deploy Center's
  // preview link carries &country=US, so every preview tab simulates - and the turkey hunt's leaderboard could never start a
  // run). The turkey leaderboard's run routes only: a run token, the time, a nickname. Its /winner/* routes carry an email
  // and stay blocked.
  var ALLOW_WRITES = [
    /^https:\/\/turkey-leaderboard-432657747828\.europe-west1\.run\.app\/run\/(start|stage|finish)(\?|$)/
  ];
  function allowedWrite(u) {
    try { var a = new URL(u, window.location.href).href; } catch (e) { return false; }
    for (var i = 0; i < ALLOW_WRITES.length; i++) if (ALLOW_WRITES[i].test(a)) return true;
    return false;
  }
  function isCartStamp(u) {
    try { return /\/cart\/(update|change)(\.js)?$/.test(new URL(u, window.location.href).pathname); }
    catch (e) { return false; }
  }

  var _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function (input, init) {
      try {
        var u = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        // Cross-origin write = a leak path (identity / analytics / restock /
        // Drip / db-app). ALLOWLIST: only same-origin writes and any read pass.
        if (!sameOrigin(u) && isWriteMethod(method) && !allowedWrite(u)) {
          blocked++; paint();
          return Promise.resolve(new Response('{"ok":false,"_sim_blocked":true}',
            { status: 200, headers: { 'Content-Type': 'application/json' } }));
        }
        // Same-origin cart note-attribute stamp: strip the identity so a real
        // checkout can't tag the order with the simulated person; keep the cart
        // operation itself working.
        if (sameOrigin(u) && isWriteMethod(method) && isCartStamp(u) && init && typeof init.body === 'string') {
          try {
            var b = JSON.parse(init.body);
            if (b && b.attributes && (b.attributes.visitorID || b.attributes.SE_SessionId)) {
              delete b.attributes.visitorID; delete b.attributes.SE_SessionId;
              blocked++; paint();
              var ni = {}; for (var kk in init) if (Object.prototype.hasOwnProperty.call(init, kk)) ni[kk] = init[kk];
              ni.body = JSON.stringify(b);
              init = ni;
            }
          } catch (e) {}
        }
      } catch (e) {}
      return _fetch.call(this, input, init);
    };
  }
  var _beacon = navigator.sendBeacon ? navigator.sendBeacon.bind(navigator) : null;
  if (_beacon) {
    navigator.sendBeacon = function (u, data) {
      try { if (!sameOrigin(u)) { blocked++; paint(); return true; } } catch (e) {}
      return _beacon(u, data);
    };
  }

  // ---- the fold: the simulation lives INSIDE the preview build dot --------
  // No bar across the page any more (Dorian, 2026-10-08: "the exit simulation
  // button is annoying … make it inside of the green preview dot folded
  // there"). While a simulation is active the preview build marker gets an
  // amber ring and becomes clickable; a click unfolds a small card: who / which
  // country is simulated, how many writes were blocked, and "Exit simulation".
  // When the marker is not on screen (switched off in the panel, or the
  // session pill took its place) a dot of our own, drawn the same, stands in.
  // The marker's own script rewrites its text when the panel config arrives,
  // so nothing is put INSIDE it: only its style, title and a click listener.
  var RING = '0 0 0 2px #f5a623, 0 1px 3px rgba(0,0,0,.4)';
  var hostEl = null, ownDot = null, fold = null, foldText = null;

  function simText() {
    var who = state.email ? (' as ' + state.email) : '';
    var co = state.country ? (' · country ' + state.country) : '';
    return 'Simulation' + who + co + ' — nothing is being saved (' + blocked + ' write' +
      (blocked === 1 ? '' : 's') + ' blocked)';
  }
  function shownMarker() {
    var m = document.getElementById('preview-build-marker');
    if (!m || m.hasAttribute('hidden')) return null;
    try { if (window.getComputedStyle(m).display === 'none') return null; } catch (e) { return null; }
    return m;
  }
  function standIn() {
    if (!ownDot) {
      ownDot = document.createElement('div');
      ownDot.textContent = '\u25CF';
      ownDot.setAttribute('style', 'position:fixed;right:6px;bottom:6px;z-index:2147483647;background:#111;' +
        'color:#0f0;font:11px/1.3 ui-monospace,Menlo,Consolas,monospace;padding:3px 6px;border-radius:4px;' +
        'opacity:.9;white-space:nowrap');
      document.body.appendChild(ownDot);
    }
    ownDot.style.display = 'block';
    return ownDot;
  }
  function arm(el) {
    el.style.pointerEvents = 'auto';
    el.style.cursor = 'pointer';
    el.style.boxShadow = RING;
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', 'Simulation: show details');
    if (!el.__agbSimArmed) {
      el.__agbSimArmed = true;
      el.__agbSimTitle = el.title || '';
      el.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); toggle();
      });
    }
    el.title = (el.__agbSimTitle ? el.__agbSimTitle + '\n' : '') + simText() + ' — click for details';
  }
  function placeHost() {
    try {
      if (!document.body) return;
      var m = shownMarker();
      if (m) { if (ownDot) ownDot.style.display = 'none'; hostEl = m; }
      else hostEl = standIn();
      arm(hostEl);
      if (fold && !fold.hidden) position();
    } catch (e) {}
  }
  function ensureFold() {
    if (fold) return;
    fold = document.createElement('div');
    fold.hidden = true;
    fold.setAttribute('role', 'dialog');
    fold.setAttribute('aria-label', 'Simulation');
    fold.setAttribute('style', 'position:fixed;z-index:2147483647;max-width:320px;background:#1c1c1c;color:#fff;' +
      'font:12px/1.45 system-ui,-apple-system,sans-serif;padding:9px 12px;border-radius:6px;' +
      'box-shadow:0 4px 18px rgba(0,0,0,.45);border:1px solid #f5a623');
    foldText = document.createElement('div');
    fold.appendChild(foldText);
    var a = document.createElement('a');
    a.href = '?sim_off=1';
    a.textContent = 'Exit simulation';
    a.setAttribute('style', 'display:inline-block;margin-top:6px;color:#92ae74;font-weight:700;text-decoration:underline');
    fold.appendChild(a);
    document.body.appendChild(fold);
    document.addEventListener('click', function (e) {
      if (!fold.hidden && !fold.contains(e.target) && e.target !== hostEl) fold.hidden = true;
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') fold.hidden = true; });
    window.addEventListener('resize', function () { if (!fold.hidden) position(); });
  }
  // Next to the dot, on the side with room: above it in the lower half of the
  // screen, below it in the upper half; aligned to its outer edge.
  function position() {
    if (!fold || !hostEl) return;
    var r = hostEl.getBoundingClientRect();
    // The layout viewport (no scrollbar): what fixed right/bottom are measured from.
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var vh = document.documentElement.clientHeight || window.innerHeight;
    fold.style.top = fold.style.bottom = fold.style.left = fold.style.right = 'auto';
    if (r.top > vh / 2) fold.style.bottom = Math.max(4, vh - r.top + 6) + 'px';
    else fold.style.top = (r.bottom + 6) + 'px';
    if (r.left + r.width / 2 > vw / 2) fold.style.right = Math.max(4, vw - r.right) + 'px';
    else fold.style.left = Math.max(4, r.left) + 'px';
  }
  function toggle() {
    ensureFold();
    fold.hidden = !fold.hidden;
    if (!fold.hidden) { paint(); position(); }
  }
  function paint() {
    try {
      if (foldText) foldText.textContent = simText();
      if (hostEl) arm(hostEl);
    } catch (e) {}
  }
  function start() { placeHost(); paint(); }
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
  // The marker can appear, move, hide or be taken over by the session pill
  // after the panel config arrives: follow it.
  document.addEventListener('agb:preview-build-ready', function () { setTimeout(placeHost, 0); });
  window.addEventListener('load', placeHost);
})();
